import { useState, type ReactNode } from 'react';
import { Download, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { cn } from '@/lib/cn';
import { downloadCSV } from '@/lib/csv';
import { notify } from '@/lib/toast';

export interface DataColumn<T> {
  header: string;
  align?: 'left' | 'right';
  /** Rendered cell (formatted / styled). */
  cell: (row: T) => ReactNode;
  /** Raw value used for CSV export. */
  csv: (row: T) => string | number | null | undefined;
}

interface PaginatedDataTableProps<T> {
  /** Rows for the current page (already limited server-side). */
  data: T[];
  columns: DataColumn<T>[];
  /** Total row count across all pages (drives the pager). */
  total: number;
  page: number;
  onPage: (page: number) => void;
  pageSize?: number;
  loading?: boolean;
  /** Base filename for the CSV export (a date stamp + `.csv` is appended). */
  exportName: string;
  /** Fetches the full, unpaginated dataset for CSV download. */
  onDownloadAll: () => Promise<T[]>;
  emptyLabel?: string;
}

/**
 * Server-driven paginated table: renders the page the API returned (default
 * 20 rows) and exposes a one-click CSV export that fetches the *entire*
 * dataset via `onDownloadAll`. Columns declare both a rendered `cell` and a
 * raw `csv` value so the export carries unformatted numbers.
 */
export function PaginatedDataTable<T>({
  data,
  columns,
  total,
  page,
  onPage,
  pageSize = 20,
  loading,
  exportName,
  onDownloadAll,
  emptyLabel = 'No data in range.',
}: PaginatedDataTableProps<T>) {
  const [downloading, setDownloading] = useState(false);
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const rows = await onDownloadAll();
      if (rows.length === 0) {
        notify.info('Nothing to export for the current filters.');
        return;
      }
      downloadCSV(
        exportName,
        columns.map((c) => c.header),
        rows.map((row) => columns.map((c) => c.csv(row))),
      );
    } catch {
      notify.error('Export failed. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div>
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="text-[11px] text-muted-foreground">
          {total} {total === 1 ? 'row' : 'rows'}
        </span>
        <Button
          variant="outline"
          size="sm"
          onClick={() => void handleDownload()}
          disabled={downloading || total === 0}
          aria-label={`Download ${exportName} as CSV`}
        >
          {downloading ? (
            <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
          ) : (
            <Download className="mr-1 h-3.5 w-3.5" />
          )}
          Download CSV
        </Button>
      </div>

      {loading ? (
        <Skeleton className="h-[220px] w-full" />
      ) : data.length === 0 ? (
        <p className="py-6 text-center text-sm text-muted-foreground">{emptyLabel}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border text-[10px] uppercase tracking-wider text-muted-foreground">
                {columns.map((c, i) => (
                  <th
                    key={i}
                    className={cn('px-2 py-1.5 font-semibold', c.align === 'right' ? 'text-right' : 'text-left')}
                  >
                    {c.header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map((row, ri) => (
                <tr key={ri} className="border-b border-border/50 last:border-0">
                  {columns.map((c, ci) => (
                    <td
                      key={ci}
                      className={cn(
                        'px-2 py-1.5',
                        c.align === 'right' ? 'kpi-num text-right tabular-nums' : 'text-foreground',
                        ci === 0 ? 'max-w-[18rem] truncate' : '',
                      )}
                    >
                      {c.cell(row)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 ? (
        <div className="mt-2 flex items-center justify-between border-t border-border pt-2 text-xs">
          <span className="text-muted-foreground">
            Page <span className="kpi-num">{page}</span> of <span className="kpi-num">{totalPages}</span>
          </span>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => onPage(page - 1)}>
              Prev
            </Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => onPage(page + 1)}>
              Next
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
